// TODO: Need to move this file to a more common location for DeckManagement, as some of these constants are used
//       outside of Page/SidePanel. Leaving this file here for now to prevent issues/conflicts with upcoming milestone.

import {
  DatasetSource,
  DenominatorLabelValues,
  GalaxyKPIDrivers,
  HctrKPIDrivers,
} from '@services/api/deckManagement/graphql/schema/API';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { flatten, isNil, uniqBy } from 'lodash';
import {
  BaseKpiDimensionFieldTypes,
  KpiDimension,
  KpiDimensionConfig,
  KpiDimensionConfigMap,
  PageSidePanelDropDown,
} from 'types/PageSidePanel.types';

/**
 * Number of spaces per indent level for KPI name indentation rendering and export.
 */
export const KPI_NAME_INDENT_SPACES = 4;

/**
 * For pages with empty page drivers, these should be used as the default
 * (bmillerv@ on 2025-10-27)
 */
export const DEFAULT_PAGE_DRIVERS: GalaxyKPIDrivers = {
  __typename: 'GalaxyKPIDrivers',
  account: null,
  account_hierarchy: null,
  location: 'LTOT',
  location_hierarchy: 'COGNOS_LOCATION_HIERARCHY',
  costcenter: 'CICC',
  costcenter_hierarchy: 'COGNOS_COST CENTER CIOS_HIERARCHY',
  company: 'TC',
  company_hierarchy: 'COGNOS_COMPANY_HIERARCHY',
  project: 'TTOT',
  project_hierarchy: 'COGNOS_PROJECT_HIERARCHY',
  channel: 'VTOT',
  channel_hierarchy: 'COGNOS_CHANNEL_HIERARCHY',
  product: 'PTOT',
  product_hierarchy: 'COGNOS_PRODUCT_HIERARCHY',
  costtype: 'All CostType',
  costpool: 'All CostPool',
  fieldtype: 'All CorpFieldType',
  datasource: 'DS001',
  datasource_hierarchy: 'COGNOS_DATASOURCE_CORP_HIERARCHY',
  revgp: 'L10000',
  revgp_hierarchy: 'COGNOS_REVGP_SEGMENT_HIERARCHY',
};

/**
 * Available denominator label options for percentage calculations.
 */
export const DenominatorLabels: Record<DenominatorLabelValues, string> = {
  [DenominatorLabelValues.REVENUE]: '% Revenue',
  [DenominatorLabelValues.GROSS_MARGIN]: '% Gross Margin',
  [DenominatorLabelValues.OPERATING_MARGIN]: 'Operating Margin',
};

/**
 * Deckline Types which are excluded from having Denominator Configuration
 */
export const EXCLUDED_DECKLINES_FOR_DENOMINATORS: PageSidePanelDropDown[] = [
  PageSidePanelDropDown.NONE,
  PageSidePanelDropDown.BLANK_LINE,
];

/**
 * Datasets whose % denominator drivers can be set on a page. Add a dataset here to enable its denominator.
 */
export const DENOMINATOR_DATASETS: DatasetSource[] = [DatasetSource.GALAXY];

/** The note shown for a page dataset whose denominator drivers are not supported yet. */
export const denominatorNotEnabledMessage = (dataset: DatasetSource) =>
  `${resolveDatasetSourceLabel(dataset)} is not enabled for denominator drivers yet`;

export const REPORTING_CURRENCY_VALUE = 'USD';

/**
 * Default drivers seeded when an HCTR dataset source is first added to a Page.
 *
 * The three HCTR-only dimensions are pre-filled with their fixed reporting defaults
 * (measure=SH1000, transferDirection=TotalAdjusted, workflow=Approved); shared dimensions
 * (company/costcenter/location/costtype) currently reuse the Galaxy defaults until HCTR diverges.
 */
export const DEFAULT_HCTR_PAGE_DRIVERS: HctrKPIDrivers = {
  __typename: 'HctrKPIDrivers',
  measure_hierarchy: 'COGNOS_HCTR_MTRF_REPORTING_HIERARCHY',
  measure: 'SH1000',
  transferDirection_hierarchy: 'COGNOS_HCTR_TRF_DIRECTION_HIERARCHY',
  transferDirection: 'TotalAdjusted',
  workflow_hierarchy: 'COGNOS_HCTR_WORKFLOW_HIERARCHY',
  workflow: 'Approved',

  // Common defaults with GALAXY Drivers. Update later if HCTR defaults diverge from Galaxy.
  company_hierarchy: DEFAULT_PAGE_DRIVERS.company_hierarchy,
  company: DEFAULT_PAGE_DRIVERS.company,
  costcenter_hierarchy: DEFAULT_PAGE_DRIVERS.costcenter_hierarchy,
  costcenter: DEFAULT_PAGE_DRIVERS.costcenter,
  location_hierarchy: DEFAULT_PAGE_DRIVERS.location_hierarchy,
  location: DEFAULT_PAGE_DRIVERS.location,
  costtype: DEFAULT_PAGE_DRIVERS.costtype,
  fieldtype: DEFAULT_PAGE_DRIVERS.fieldtype,
};

/**
 * The default driver set to seed when a given {@link DatasetSource} is first configured on a Page.
 * @see {@link DEFAULT_PAGE_DRIVERS} (Galaxy) and {@link DEFAULT_HCTR_PAGE_DRIVERS} (HCTR).
 */
export const DATASET_DEFAULT_PAGE_DRIVERS: Record<DatasetSource, GalaxyKPIDrivers | HctrKPIDrivers> = {
  [DatasetSource.GALAXY]: DEFAULT_PAGE_DRIVERS,
  [DatasetSource.HCTR]: DEFAULT_HCTR_PAGE_DRIVERS,
};

/**
 * Deckline Dimension Render Configuration (All Dataset). NOTE: Order does NOT matter here.
 * Use {@link KPI_DIMENSION_RENDER_LIST} for rendering order in the side panel.
 */
export const KPI_DIMENSION_CONFIG_MAP: KpiDimensionConfigMap = {
  [KpiDimension.ACCOUNT]: {
    key: KpiDimension.ACCOUNT,
    formulaKey: 'Account',
    label: 'Account',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'account',
    },
    decklineDrivers: {
      hierarchyAccessor: 'account_hierarchy',
      valueAccessor: 'account',
    },
  },
  [KpiDimension.REVGP]: {
    key: KpiDimension.REVGP,
    formulaKey: 'REVGP',
    label: 'RevGP',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'revgp',
    },
    decklineDrivers: {
      hierarchyAccessor: 'revgp_hierarchy',
      valueAccessor: 'revgp',
    },
  },
  [KpiDimension.COST_CENTER]: {
    key: KpiDimension.COST_CENTER,
    formulaKey: 'Costcenter',
    label: 'Cost Center',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'costcenter',
    },
    decklineDrivers: {
      hierarchyAccessor: 'costcenter_hierarchy',
      valueAccessor: 'costcenter',
    },
  },
  [KpiDimension.COMPANY]: {
    key: KpiDimension.COMPANY,
    formulaKey: 'Company',
    label: 'Company',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'company',
    },
    decklineDrivers: {
      hierarchyAccessor: 'company_hierarchy',
      valueAccessor: 'company',
    },
  },
  [KpiDimension.PROJECT]: {
    key: KpiDimension.PROJECT,
    formulaKey: 'Project',
    label: 'Project',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'project',
    },
    decklineDrivers: {
      hierarchyAccessor: 'project_hierarchy',
      valueAccessor: 'project',
    },
  },
  [KpiDimension.REPORTING_CURRENCY]: {
    key: KpiDimension.REPORTING_CURRENCY,
    label: 'Reporting Currency',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.ReadOnly,
      staticValue: REPORTING_CURRENCY_VALUE,
    },
  },
  [KpiDimension.CHANNEL]: {
    key: KpiDimension.CHANNEL,
    formulaKey: 'Channel',
    label: 'Channel',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'channel',
    },
    decklineDrivers: {
      hierarchyAccessor: 'channel_hierarchy',
      valueAccessor: 'channel',
    },
  },
  [KpiDimension.PRODUCT]: {
    key: KpiDimension.PRODUCT,
    formulaKey: 'Product',
    label: 'Product',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'product',
    },
    decklineDrivers: {
      hierarchyAccessor: 'product_hierarchy',
      valueAccessor: 'product',
    },
  },
  [KpiDimension.COST_TYPE]: {
    key: KpiDimension.COST_TYPE,
    formulaKey: 'CostType',
    label: 'Cost Type',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.Select,
      options: [{ value: 'All CostType' }, { value: 'F' }, { value: 'V' }],
      rootValue: 'All CostType',
    },
    decklineDrivers: {
      valueAccessor: 'costtype',
    },
  },
  [KpiDimension.COST_POOL]: {
    key: KpiDimension.COST_POOL,
    formulaKey: 'CostPool',
    label: 'Cost Pool',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.Select,
      options: [{ value: 'All CostPool' }, { value: 'DIGMUSVIDLF-1' }, { value: 'INTL-07' }],
      rootValue: 'All CostPool',
    },
    decklineDrivers: {
      valueAccessor: 'costpool',
    },
  },
  [KpiDimension.DATA_SOURCE]: {
    key: KpiDimension.DATA_SOURCE,
    formulaKey: 'Datasource',
    label: 'Data Source',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'datasource',
    },
    decklineDrivers: {
      hierarchyAccessor: 'datasource_hierarchy',
      valueAccessor: 'datasource',
    },
  },
  [KpiDimension.LOCATION]: {
    key: KpiDimension.LOCATION,
    formulaKey: 'Location',
    label: 'Location',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'location',
    },
    decklineDrivers: {
      hierarchyAccessor: 'location_hierarchy',
      valueAccessor: 'location',
    },
  },
  [KpiDimension.MEASURE]: {
    key: KpiDimension.MEASURE,
    formulaKey: 'Measure',
    label: 'Measure',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'hctr',
      rootNodesFilter: ['COGNOS_HCTR_MTRF_REPORTING_HIERARCHY-Total_MTRF_REPORTING'],
    },
    decklineDrivers: {
      hierarchyAccessor: 'measure_hierarchy',
      valueAccessor: 'measure',
    },
  },
  [KpiDimension.TRANSFER_DIRECTION]: {
    key: KpiDimension.TRANSFER_DIRECTION,
    formulaKey: 'TransferDirection',
    label: 'Transfer Direction',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'hctr',
      rootNodesFilter: ['COGNOS_HCTR_TRF_DIRECTION_HIERARCHY-Total_TRF_Direction'],
    },
    decklineDrivers: {
      hierarchyAccessor: 'transferDirection_hierarchy',
      valueAccessor: 'transferDirection',
    },
  },
  [KpiDimension.WORKFLOW]: {
    key: KpiDimension.WORKFLOW,
    formulaKey: 'Workflow',
    label: 'Workflow',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.DimensionPicker,
      dimensionName: 'hctr',
      rootNodesFilter: ['COGNOS_HCTR_WORKFLOW_HIERARCHY-Total_WF'],
    },
    decklineDrivers: {
      hierarchyAccessor: 'workflow_hierarchy',
      valueAccessor: 'workflow',
    },
  },
  [KpiDimension.FIELD_TYPE]: {
    key: KpiDimension.FIELD_TYPE,
    formulaKey: 'FieldType',
    label: 'Field Type',
    baseKpiField: {
      type: BaseKpiDimensionFieldTypes.Select,
      options: [{ value: 'All CorpFieldType' }, { value: 'Corp' }, { value: 'Field' }],
      rootValue: 'All CorpFieldType',
    },
    decklineDrivers: {
      valueAccessor: 'fieldtype',
    },
  },
};

/**
 * The correct order to render Galaxy deckline dimensions defined in {@link KPI_DIMENSION_CONFIG_MAP}.
 * 1. Order matters.
 * 2. If you do NOT want to render a dimension for Galaxy, do not include it here.
 * @see {@link PageSidePanel} for originally-intended usage.
 */
export const KPI_DIMENSION_RENDER_LIST: KpiDimensionConfig[] = [
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.ACCOUNT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.REVGP],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_CENTER],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COMPANY],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.PROJECT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.REPORTING_CURRENCY],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.CHANNEL],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.PRODUCT],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_TYPE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_POOL],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.DATA_SOURCE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.LOCATION],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.FIELD_TYPE],
];

/**
 * The correct order to render HCTR deckline dimensions defined in {@link KPI_DIMENSION_CONFIG_MAP}.
 * 1. Order matters.
 * 2. If you do NOT want to render a dimension for HCTR, do not include it here.
 * @see {@link PageSidePanel} for originally-intended usage.
 */
export const HCTR_DIMENSION_RENDER_LIST: KpiDimensionConfig[] = [
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.MEASURE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.TRANSFER_DIRECTION],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.WORKFLOW],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COMPANY],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_CENTER],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.LOCATION],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.COST_TYPE],
  KPI_DIMENSION_CONFIG_MAP[KpiDimension.FIELD_TYPE],
];

/**
 * Mapping of DatasetSource to the correct KPI dimension render list.
 */
export const KPI_DIMENSION_RENDER_MAP: Record<DatasetSource, KpiDimensionConfig[]> = {
  [DatasetSource.GALAXY]: KPI_DIMENSION_RENDER_LIST,
  [DatasetSource.HCTR]: HCTR_DIMENSION_RENDER_LIST,
};

/**
 * Maintains a distinct list of all dimensions that are used by KPI_DIMENSION_RENDER_MAP.
 * @see {@link KPI_DIMENSION_RENDER_LIST} and {@link HCTR_DIMENSION_RENDER_LIST} for the list of dimensions used by the side panel.
 */
const allRenderedDimensions = uniqBy(
  flatten(Object.values(KPI_DIMENSION_RENDER_MAP)),
  (dimensionConfig) => dimensionConfig.key,
);

/**
 * Maintains a distinct list of all dimensions that are used by KPI_DIMENSION_RENDER_MAP and supported by the DimSum/DimensionPicker.
 * This is used to filter out dimensions that are not supported by DimSum.
 * @see {@link KPI_DIMENSION_RENDER_LIST} for the list of dimensions used by the side panel.
 * @see {@link DeckContextProvider} for initial intended usage
 */
export const KPI_SUPPORTED_DIMSUM_DIMENSIONS = allRenderedDimensions
  .map((dimensionConfig) =>
    dimensionConfig.baseKpiField.type === BaseKpiDimensionFieldTypes.DimensionPicker
      ? dimensionConfig.baseKpiField.dimensionName
      : null,
  )
  .filter((dimensionName) => !isNil(dimensionName));
